/**
 * Sprint 24.15.2 — hire ↔ Connections round-trip contract.
 *
 * "Open Connections" from the equipment-missing modal navigates AWAY from the
 * (full-route) hire wizard, so the in-progress customise form must survive the
 * trip in sessionStorage and be picked back up on remount. This module is the
 * single source of truth for the storage key, the deep-link return path, and the
 * staleness rule — shared by HireWizardTemplateCustomise (writer/reader) and
 * ConnectionsDashboardPage (reads the template id out of `return_to`).
 */

import type { TierChoice } from "../../hooks/use-agent-templates";

/** The persisted customise-form state (Decision 9 — survives the round-trip). */
export interface HireTemplateDraft {
  nameOverride: string;
  reportsTo: string | null;
  addendum: string;
  tier: TierChoice;
  /** Optional-equipment kinds toggled off, keyed by kind. */
  optionalOff: Record<string, boolean>;
  /** Epoch ms — used to expire a stale draft (Decision 9 risk row: 1h ceiling). */
  savedAt: number;
}

/** Drafts older than this when read back are ignored (user wandered off). */
export const HIRE_DRAFT_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

function draftKey(templateId: string): string {
  return `hire_template_draft_${templateId}`;
}

/** Persist the in-progress customise form before navigating to Connections. */
export function saveHireDraft(templateId: string, draft: Omit<HireTemplateDraft, "savedAt">): void {
  try {
    const payload: HireTemplateDraft = { ...draft, savedAt: Date.now() };
    sessionStorage.setItem(draftKey(templateId), JSON.stringify(payload));
  } catch {
    /* sessionStorage may be unavailable (private mode / quota) — non-fatal. */
  }
}

/**
 * Read back a persisted draft. Returns null when absent, malformed, or older
 * than {@link HIRE_DRAFT_MAX_AGE_MS} (stale drafts are cleared on read).
 */
export function loadHireDraft(templateId: string): HireTemplateDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(templateId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HireTemplateDraft>;
    if (typeof parsed.savedAt !== "number" || Date.now() - parsed.savedAt > HIRE_DRAFT_MAX_AGE_MS) {
      clearHireDraft(templateId);
      return null;
    }
    return {
      nameOverride: typeof parsed.nameOverride === "string" ? parsed.nameOverride : "",
      reportsTo: typeof parsed.reportsTo === "string" ? parsed.reportsTo : null,
      addendum: typeof parsed.addendum === "string" ? parsed.addendum : "",
      tier:
        parsed.tier === "Fast" || parsed.tier === "Standard" || parsed.tier === "Heavy"
          ? parsed.tier
          : "Workspace",
      optionalOff:
        parsed.optionalOff && typeof parsed.optionalOff === "object"
          ? (parsed.optionalOff as Record<string, boolean>)
          : {},
      savedAt: parsed.savedAt,
    };
  } catch {
    return null;
  }
}

/** Drop a persisted draft — on successful hire or explicit cancel. */
export function clearHireDraft(templateId: string): void {
  try {
    sessionStorage.removeItem(draftKey(templateId));
  } catch {
    /* non-fatal */
  }
}

/**
 * The deep-link that re-opens the hire wizard on the customise step for a given
 * template (HireWizardPage reads `?start=customise&template=<id>`). This is the
 * `return_to` the Connections flow navigates back to.
 */
export function hireTemplateReturnPath(templateId: string): string {
  return `/hire?start=customise&template=${encodeURIComponent(templateId)}`;
}

/** Minimal connection shape for kind-matching (subset of UnifiedConnection). */
export interface MatchableConnection {
  connection_id: string;
  name?: string | null;
  server_name?: string | null;
  first_party_template_slug?: string | null;
}

function normaliseKind(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Client mirror of the worker's matchEquipmentToConnections kind-match: is an
 * equipment KIND (supporting "A OR B" alternatives) satisfied by any installed
 * connection? Used by the Connections hiring banner to drive the auto-resume
 * gate (Decision 8 — match by kind/server name, not connection id).
 */
export function kindIsInstalled(kind: string, connections: MatchableConnection[]): boolean {
  const alternatives = kind.split(/\s+or\s+/i).map(normaliseKind).filter(Boolean);
  return connections.some((c) => {
    const candidates = [c.server_name, c.first_party_template_slug, c.connection_id, c.name]
      .filter((x): x is string => typeof x === "string" && x.length > 0)
      .map(normaliseKind);
    return alternatives.some((alt) => candidates.some((cand) => cand === alt || cand.includes(alt) || alt.includes(cand)));
  });
}

/**
 * Extract the template id from a `return_to` value produced by
 * {@link hireTemplateReturnPath}. Tolerant of absolute or relative paths and a
 * leading origin. Returns null when the param is absent or unparseable.
 */
export function parseTemplateIdFromReturnTo(returnTo: string | null | undefined): string | null {
  if (!returnTo) return null;
  try {
    const q = returnTo.indexOf("?");
    if (q < 0) return null;
    const params = new URLSearchParams(returnTo.slice(q + 1));
    const id = params.get("template");
    return id && id.trim() ? id.trim() : null;
  } catch {
    return null;
  }
}
